/**
 * Plano de ação — integração com a API (issue #359).
 *
 * As treze rotas do contrato fixado em `docs/specs/359-plano-de-implementacao.md`. Duas coisas
 * a tela nunca manda: o prazo da ação, que o servidor calcula (D3), e a empresa, que vem do
 * contexto ativo da sessão.
 */

import apiClient from './apiClient';
import { FILTROS_DE_ITENS_VAZIOS } from '../types/actionPlan';
import type {
  ActionPlan,
  ActionPlanCatalogNorma,
  ActionPlanItem,
  ActionPlanSummary,
  DadosDaAcao,
  DadosDoPlano,
  FiltrosDeItens,
} from '../types/actionPlan';

export * from '../types/actionPlan';

const BASE = '/action-plans';

/**
 * Id de `BIGINT.UNSIGNED` chega ora como número, ora como texto — depende de o driver ter
 * ligado `bigNumberStrings` naquela consulta. A tela compara e indexa por ele, e `'7' === 7` é
 * falso: normalizar na fronteira é o que impede um quadro vazio sem erro nenhum.
 */
function comoTexto(id: unknown): string {
  return String(id);
}

function comoTextoOuNulo(id: unknown): string | null {
  return id === null || id === undefined ? null : String(id);
}

function normalizarPlano(bruto: ActionPlan): ActionPlan {
  return { ...bruto, id: comoTexto(bruto.id), companyId: comoTexto(bruto.companyId) };
}

function normalizarItem(bruto: ActionPlanItem): ActionPlanItem {
  return {
    ...bruto,
    id: comoTexto(bruto.id),
    actionPlanId: comoTexto(bruto.actionPlanId),
    originId: comoTextoOuNulo(bruto.originId),
  };
}

/** Só o que tem valor vira parâmetro: filtro em branco na URL vira recorte vazio no servidor. */
function comoConsulta(filtros: FiltrosDeItens): string {
  const parametros = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') parametros.set(chave, String(valor));
  }
  const consulta = parametros.toString();
  return consulta === '' ? '' : `?${consulta}`;
}

export const actionPlanService = {
  async listarPlanos(): Promise<ActionPlanSummary[]> {
    const { data } = await apiClient.get<ActionPlanSummary[]>(BASE);
    return (data ?? []).map((plano) => ({ ...plano, id: comoTexto(plano.id) }));
  },

  async obterPlano(id: string): Promise<ActionPlan> {
    const { data } = await apiClient.get<ActionPlan>(`${BASE}/${id}`);
    if (!data) throw new Error('Plano de ação não encontrado');
    return normalizarPlano(data);
  },

  async criarPlano(dados: DadosDoPlano): Promise<ActionPlan> {
    const { data } = await apiClient.post<ActionPlan>(BASE, dados);
    if (!data) throw new Error('Resposta vazia do servidor ao criar o plano de ação');
    return normalizarPlano(data);
  },

  async atualizarPlano(id: string, dados: DadosDoPlano): Promise<ActionPlan> {
    const { data } = await apiClient.put<ActionPlan>(`${BASE}/${id}`, dados);
    if (!data) throw new Error('Resposta vazia do servidor ao salvar o plano de ação');
    return normalizarPlano(data);
  },

  async removerPlano(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async listarItens(
    planoId: string,
    filtros: FiltrosDeItens = FILTROS_DE_ITENS_VAZIOS
  ): Promise<ActionPlanItem[]> {
    const { data } = await apiClient.get<ActionPlanItem[]>(
      `${BASE}/${planoId}/items${comoConsulta(filtros)}`
    );
    return (data ?? []).map(normalizarItem);
  },

  async criarItem(planoId: string, dados: DadosDaAcao): Promise<ActionPlanItem> {
    const { data } = await apiClient.post<ActionPlanItem>(`${BASE}/${planoId}/items`, dados);
    if (!data) throw new Error('Resposta vazia do servidor ao criar a ação');
    return normalizarItem(data);
  },

  async atualizarItem(
    planoId: string,
    itemId: string,
    dados: DadosDaAcao
  ): Promise<ActionPlanItem> {
    const { data } = await apiClient.put<ActionPlanItem>(
      `${BASE}/${planoId}/items/${itemId}`,
      dados
    );
    if (!data) throw new Error('Resposta vazia do servidor ao salvar a ação');
    return normalizarItem(data);
  },

  async removerItem(planoId: string, itemId: string): Promise<void> {
    await apiClient.delete(`${BASE}/${planoId}/items/${itemId}`);
  },

  /** `normas: null` traz o catálogo inteiro; uma lista traz só as normas escolhidas (D11). */
  async trazerDoCatalogo(planoId: string, normas: string[] | null): Promise<number> {
    const { data } = await apiClient.post<{ criadas: number }>(
      `${BASE}/${planoId}/items/from-catalog`,
      { normas }
    );
    return data?.criadas ?? 0;
  },

  async enviarEvidencia(planoId: string, itemId: string, arquivo: File): Promise<ActionPlanItem> {
    const { data } = await apiClient.uploadFile<ActionPlanItem>(
      `${BASE}/${planoId}/items/${itemId}/evidence`,
      arquivo
    );
    if (!data) throw new Error('Resposta vazia do servidor ao enviar a evidência');
    return normalizarItem(data);
  },

  /**
   * O anexo tem rota própria porque o gate de leitura vive nela: nada do root de arquivos
   * privados é servido como estático.
   */
  caminhoDaEvidencia(planoId: string, itemId: string): string {
    return `${BASE}/${planoId}/items/${itemId}/evidence`;
  },

  async listarCatalogo(): Promise<ActionPlanCatalogNorma[]> {
    const { data } = await apiClient.get<ActionPlanCatalogNorma[]>('/action-plan-catalog');
    return data ?? [];
  },
};

export default actionPlanService;
